import React, { useEffect, useRef, useState } from 'react';
import {ProviderConsumer} from '@fluentui/react-northstar';
import { useTranslation } from 'react-i18next';
import '../../../../../src/components/mgt.module.css';
import { PeoplePicker, PersonType, UserType } from '@microsoft/mgt-react';
import { appState } from '../../../AppState';
import { getCachedUser } from '../../cache/PlatformUserCache';
import { MeToken, ScopeTokens } from '../../utils/constants';
import { ThemeColorScheme } from '../../common/TeamsTheme';

export interface ISelectedPeople 
{
    displayName?: string;
    userPrincipalName?: string;
    givenName?: string;
    surname?: string;
    mail?: string;
    id?: string;
}


export interface ICustomPeoplePicker {
  id?:string;
  isEnabled?:boolean;
  selectedPeople?: ISelectedPeople[];
  selectionChanged: (e:any) => void;
  peoplePickerRef?: any;
  type?: PersonType;
  userType?: UserType;
  enableMeToken?:boolean;
}

// The Me scope token is matched on the literal word "me", case insensitive. #35080
const isMeSearch = (text: string) => (text ?? "").trim().toLowerCase() == MeToken.toLowerCase();
// mail stays the untranslated "Me" - it is the key the view filters are saved against.
const hasMeToken = (people: any[]) => (people ?? []).some(p => p?.mail == MeToken);

export const CustomPeoplePicker = (props: ICustomPeoplePicker) => {
    const { t } = useTranslation();
    const state = appState();
    let { id, isEnabled, selectionChanged, selectedPeople, peoplePickerRef, type, userType, enableMeToken} = props;

    const pickerRef = useRef<any>(null);
    const [currentUserName, setCurrentUserName] = useState("");

    // read by the patched loadState below so it always sees the current values
    const meTokenRef = useRef<any>(null);
    meTokenRef.current = !enableMeToken ? null : {
      ...ScopeTokens.MeTokenData,
      displayName: t('common.me'),
      jobTitle: currentUserName ? t('common.current-user', { name: currentUserName }) : ""
    };

    useEffect(()=>{
        if (!enableMeToken || !state.currentUserId) return;
        getCachedUser(state.currentUserId).then(user => setCurrentUserName(user?.FullName ?? ""));
      },[enableMeToken, state.currentUserId]);

    useEffect(()=>{
        isEnabled ??= true;

        const picker = pickerRef.current;
        if (!picker) return;

        if (peoplePickerRef) peoplePickerRef.current = picker;

        picker.selectedPeople = selectedPeople;

        if (picker.meTokenPatched) return;
        picker.meTokenPatched = true;

        // Run the stock people picker search - the exact same /me/people + /users
        // calls the create/edit ticket form makes - and only then surface the Me
        // token at the top of the results. #35080 / #35159
        const baseLoadState = picker.loadState.bind(picker);
        picker.loadState = async () => {
          await baseLoadState();

          const meToken = meTokenRef.current;
          if (!meToken) return;

          // Typed a search: only the literal word "me" brings the token back.
          if ((picker.userInput ?? "").trim() != "") {
            if (!isMeSearch(picker.userInput) || hasMeToken(picker.selectedPeople)) return;
            picker._foundPeople = [{...meToken}, ...(picker._foundPeople ?? [])];
            return;
          }

          // Nothing searched yet: Me sits at the top of the default list. Adding it
          // to defaultPeople also keeps handleFlyout from suppressing the dropdown
          // once every suggested person has been picked.
          if (!picker._isFocused) return;
          picker.defaultPeople = [{...meToken}, ...(picker.defaultPeople ?? []).filter(p => p?.mail != MeToken)];
          picker._foundPeople = picker.filterPeople(picker.defaultPeople) ?? [];
        };
      },[]);

    // MGT uppercases any subtitle it takes from jobTitle. The Me row shows the
    // signed in user's name, which reads better in normal case, so undo it for
    // that one row only.
    useEffect(()=>{
        const picker = pickerRef.current;
        const label = meTokenRef.current?.displayName;
        if (!picker || !label) return;

        picker.updateComplete?.then(() => {
          const root = picker.shadowRoot;
          if (!root) return;

          let style = root.querySelector('style[data-me-token]');
          if (!style) {
            style = document.createElement('style');
            style.setAttribute('data-me-token', '');
            root.appendChild(style);
          }
          style.textContent = `.people-person-text-area[id="${label.replace(/"/g, '\\"')}"] .people-person-job-title.uppercase { text-transform: none; }`;
        });
      },[t, currentUserName]);

    useEffect(()=>{
        if (!isEnabled && pickerRef.current) {
          pickerRef.current.selectedPeople = [];
        }
      },[isEnabled]);

    return <ProviderConsumer
          render={globalTheme => (
            <>
              <div style={{display: 'block',...{ '--input-background-color': 'var(--mgt-theme-background)' }, ...ThemeColorScheme(globalTheme.siteVariables)}}>
                <PeoplePicker 
                  {...(id ? { id } : {})}
                  disabled={!isEnabled} 
                  userType={userType ?? UserType.any} type={type ?? PersonType.any}
                  ref={pickerRef}  
                  selectedPeople={selectedPeople}
                  selectionChanged={selectionChanged}>
                </PeoplePicker>
              </div>
              
            </>
          )}/>

}
